import type { Feedback, FeedbackCategory } from '@buakei/types';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  PermissionDeniedState,
} from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@/auth/session';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { adminApi, feedbackApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Feedback and support (M6, ADR-030): any verified reader writes to the platform team; platform
 * administrators read the inbox under Operations → Support and close each item with a note.
 */

export const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  problem: 'Something is not working',
  question: 'A question about using Buakei AI',
  suggestion: 'A suggestion',
  access: 'Access to a workspace or role',
  account_deletion: 'Delete my account',
};

export function FeedbackPage() {
  const tokens = useReaderTokens();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const categoryId = useId();
  const messageId = useId();
  const [category, setCategory] = useState<FeedbackCategory>('problem');
  const [message, setMessage] = useState('');
  const send = useMutation({
    mutationFn: () =>
      feedbackApi.send(tokens!, {
        category,
        message,
        ...(from && /^\/[A-Za-z0-9/_\-.~:@]*$/.test(from) ? { page: from.slice(0, 200) } : {}),
      }),
  });
  if (!tokens) {
    return (
      <Page title="Send feedback">
        <EmptyState title="Verify your email address to send feedback" />
      </Page>
    );
  }
  return (
    <Page
      title="Send feedback"
      description="Tell the Buakei AI team what is not working, ask a question, or suggest an improvement."
    >
      {send.isSuccess ? (
        <p role="status" className="text-ink-900">
          Thank you. The team will reply by email, usually within two working days.
        </p>
      ) : (
        <form
          className="flex max-w-2xl flex-col gap-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <div className="flex flex-col gap-1">
            <label htmlFor={categoryId} className="text-sm font-medium text-ink-900">
              About
            </label>
            <select
              id={categoryId}
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
              className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
            >
              {(Object.keys(CATEGORY_LABEL) as FeedbackCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={messageId} className="text-sm font-medium text-ink-900">
              Message
            </label>
            <textarea
              id={messageId}
              required
              minLength={10}
              maxLength={2000}
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What happened, what you expected, and when"
              className="rounded-md border border-line-200 bg-white p-2 text-ink-900 placeholder:text-muted-600"
            />
            <p className="text-sm text-muted-600">
              Do not include client names, case facts or confidential documents. To report a wrong
              Act or judgment, use "Report a problem" on that document instead.
            </p>
          </div>
          <Button
            type="submit"
            className="self-start"
            disabled={send.isPending || message.trim().length < 10}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
          {send.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {send.error.message}
            </p>
          ) : null}
        </form>
      )}
    </Page>
  );
}

function SupportItem({
  item,
  tokens,
}: {
  item: Feedback;
  tokens: NonNullable<ReturnType<typeof useReaderTokens>>;
}) {
  const queryClient = useQueryClient();
  const noteId = useId();
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');
  const close = useMutation({
    mutationFn: () => adminApi.closeFeedback(tokens, item.id, note),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });
  return (
    <li className="flex flex-col gap-2 p-4">
      <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-navy-900">
        <Badge tone={item.status === 'open' ? 'danger' : 'plain'}>
          {item.status === 'open' ? 'Open' : 'Closed'}
        </Badge>
        {CATEGORY_LABEL[item.category]}
      </span>
      <span className="text-xs text-muted-600">
        {new Date(item.createdAt).toLocaleString('en-GB')} · {item.fromName ?? 'No name'} ·{' '}
        {item.fromEmail ?? 'no email'}
        {item.page ? ` · from ${item.page}` : ''}
      </span>
      <p className="text-sm whitespace-pre-wrap text-ink-900">{item.message}</p>
      {item.closing ? (
        <p className="text-sm text-muted-600">Closed: {item.closing.note}</p>
      ) : closing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            close.mutate();
          }}
        >
          <label htmlFor={noteId} className="text-sm font-medium text-ink-900">
            What was done
          </label>
          <textarea
            id={noteId}
            required
            minLength={3}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-md border border-line-200 bg-white p-2 text-ink-900"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={close.isPending || note.trim().length < 3}>
              Close
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setClosing(false)}>
              Cancel
            </Button>
          </div>
          {close.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {close.error.message}
            </p>
          ) : null}
        </form>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="self-start"
          onClick={() => setClosing(true)}
        >
          Close with a note
        </Button>
      )}
    </li>
  );
}

export function SupportInboxPage() {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  const isAdmin = Boolean(session?.memberships.some((m) => m.roles.includes('platform_admin')));
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const statusId = useId();
  const items = useQuery({
    queryKey: ['admin', 'feedback', status],
    queryFn: () => adminApi.feedback(tokens!, status),
    enabled: Boolean(tokens && isAdmin),
  });
  if (!tokens || !isAdmin) {
    return (
      <Page title="Support">
        <PermissionDeniedState title="Support is for platform administrators" />
      </Page>
    );
  }
  return (
    <Page
      title="Support"
      description="Feedback and requests from readers. Reply by email, then close each item with what was done."
      width="wide"
    >
      <div className="flex flex-col gap-1 self-start">
        <label htmlFor={statusId} className="text-sm font-medium text-navy-900">
          Show
        </label>
        <select
          id={statusId}
          value={status}
          onChange={(e) => setStatus(e.target.value as 'open' | 'closed')}
          className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
        >
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      {items.isPending ? <LoadingState label="Loading support items" /> : null}
      {items.isError ? (
        <ErrorState title="Support items could not be loaded" onRetry={() => void items.refetch()}>
          {items.error.message}
        </ErrorState>
      ) : null}
      {items.data && items.data.items.length === 0 ? (
        <EmptyState title={status === 'open' ? 'Nothing open' : 'Nothing closed yet'} />
      ) : null}
      {items.data && items.data.items.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
          {items.data.items.map((item) => (
            <SupportItem key={item.id} item={item} tokens={tokens} />
          ))}
        </ul>
      ) : null}
    </Page>
  );
}
